import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Icon } from "@iconify-icon/react";
import { format } from "date-fns";

import { useWorkspaceSettingsStore } from "../../store/useWorkspaceSettingsStore";

import { SettingsCard } from "./SettingsCard";
import { LeaveWorkspaceDialog } from "./LeaveWorkspaceDialog";

import { Button } from "@/components/ui/button";
import { axiosApiInstance } from "@/libs/axios";
import { WORKSPACES_API_ENDPOINTS } from "@/constants/api";
import useUserStore from "@/features/auth/store/useUserStore";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";
import { toast } from "@/utils/customToast";

export const UserStatusCard = () => {
  const { t, i18n } = useTranslation(["workspaces"]);
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { settings, refetch: refetchSettings } = useWorkspaceSettingsStore();
  const { refetch: refetchWorkspaceInfo, data: workspaceInfo } = useWorkspaceInformationStore();
  const { user } = useUserStore();
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const [isRejoining, setIsRejoining] = useState(false);

  if (!settings) return null;

  const { user_status } = settings;
  const isStudent = user?.role === "student";

  const handleRejoinWorkspace = async () => {
    if (!workspaceId) return;

    setIsRejoining(true);
    try {
      const endpoint = isStudent
        ? WORKSPACES_API_ENDPOINTS(workspaceId).STUDENT.REJOIN
        : WORKSPACES_API_ENDPOINTS(workspaceId).TUTOR.REJOIN;

      await axiosApiInstance.post(endpoint);

      // Refetch both workspace basics and settings data to ensure all status fields are updated
      await refetchWorkspaceInfo(workspaceId);
      await refetchSettings(workspaceId);

      toast.success(t("rejoinWorkspace.toastSuccess.title"), {
        description: t("rejoinWorkspace.toastSuccess.description"),
      });
    } catch (error: any) {
      const errorCode = error?.response?.data?.code;
      const errorMsg = errorCode
        ? t(`rejoinWorkspace.errors.${errorCode}` as any, {
            defaultValue: "An unexpected error occurred. Please try again.",
          })
        : t("rejoinWorkspace.toastError.description");

      toast.error(t("rejoinWorkspace.toastError.title"), {
        description: errorMsg,
      });
    } finally {
      setIsRejoining(false);
    }
  };

  const getStatusInfo = () => {
    const knowledgeCut = workspaceInfo?.knowledge_cut;
    const formattedKnowledgeCut = knowledgeCut
      ? format(new Date(knowledgeCut), "PPP", {
          locale: getDateFnsLocale(i18n.language),
        })
      : null;

    if (user_status.is_blocked_for_user) {
      return {
        icon: "mdi:block-helper",
        title: t("workspaces:settings.userStatus.blocked", "Your Status: Blocked"),
        variant: "destructive" as const,
        description: t("workspaces:settings.userStatus.blockedDescription", {
          defaultValue:
            "You have limited access to this workspace, you can only see records up to {{knowledgeCut}}",
          knowledgeCut: formattedKnowledgeCut,
        }),
        topCircle: "from-red-600 to-pink-500",
        bottomCircle: "from-red-500 to-orange-500",
        showLeaveButton: false,
        showRejoinButton: false,
      };
    }

    if (user_status.has_user_left) {
      return {
        icon: "mdi:exit-to-app",
        title: t("workspaces:settings.userStatus.left", "Your Status: Inactive"),
        variant: "secondary" as const,
        description: t("workspaces:settings.userStatus.leftDescription", {
          defaultValue:
            "You have limited access to this workspace, you can only see records up to {{knowledgeCut}}",
          knowledgeCut: formattedKnowledgeCut,
        }),
        topCircle: "from-gray-600 to-gray-500",
        bottomCircle: "from-gray-500 to-gray-400",
        showLeaveButton: false,
        showRejoinButton: true,
      };
    }

    if (!user_status.is_active_for_user) {
      return {
        icon: "mdi:pause-circle",
        title: t("workspaces:settings.userStatus.inactive", "Your Status: Inactive"),
        variant: "secondary" as const,
        description: t("workspaces:settings.userStatus.inactiveDescription", {
          defaultValue:
            "You have limited access to this workspace, you can only see records up to {{knowledgeCut}}",
          knowledgeCut: formattedKnowledgeCut,
        }),
        topCircle: "from-yellow-600 to-orange-500",
        bottomCircle: "from-yellow-500 to-red-500",
        showLeaveButton: false,
        showRejoinButton: false,
      };
    }

    return {
      icon: "mdi:check-circle",
      title: t("workspaces:settings.userStatus.active", "Your Status: Active"),
      variant: "default" as const,
      description: t("workspaces:settings.userStatus.activeDescription", {
        defaultValue: "You have full access to this workspace",
      }),
      topCircle: "from-green-600 to-blue-500",
      bottomCircle: "from-green-500 to-teal-500",
      showLeaveButton: true,
      showRejoinButton: false,
    };
  };

  const statusInfo = getStatusInfo();

  const action = (
    <div className="flex items-center gap-2">
      {statusInfo.showLeaveButton && (
        <Button
          className="h-6 gap-1 px-2 text-xs"
          size="sm"
          variant="destructive"
          onClick={() => setIsLeaveDialogOpen(true)}
        >
          <Icon icon="mdi:exit-to-app" />
          {t("workspaces:leaveWorkspace.leave")}
        </Button>
      )}
      {statusInfo.showRejoinButton && (
        <Button
          className="h-6 gap-1 px-2 text-xs"
          disabled={isRejoining}
          size="sm"
          variant="foreground"
          onClick={handleRejoinWorkspace}
        >
          {isRejoining ? (
            <Icon className="animate-spin" icon="lucide:loader-2" />
          ) : (
            <Icon icon="mdi:account-plus" />
          )}
          {t("rejoinWorkspace.rejoin")}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <SettingsCard
        action={action}
        bottomCircle={statusInfo.bottomCircle}
        bottomCirclePlacement="bottom-right"
        bottomCircleSize="h-16 w-16"
        description={statusInfo.description}
        icon={statusInfo.icon}
        title={statusInfo.title}
        topCircle={statusInfo.topCircle}
        topCirclePlacement="top-left"
        topCircleSize="h-20 w-20"
      />
      <LeaveWorkspaceDialog
        isOpen={isLeaveDialogOpen}
        onClose={() => setIsLeaveDialogOpen(false)}
      />
    </>
  );
};
